import type { Settings } from '../../context/settingsContext';

/**
 * Papers that must keep the filter on themselves, for one of two reasons.
 *
 * Placement: MUI positions these itself — fixed or sticky app bars, fixed drawers,
 * popper-anchored menus and popovers, dialogs. Forcing `position: relative` on them would drop
 * them back into normal flow.
 *
 * Pseudo-element already in use: Accordion draws its top divider with `::before`, so claiming it
 * here would fight that rule.
 *
 * These stay backdrop roots, exactly as every Paper was before. Nothing regresses; they simply
 * don't gain the ability to host blurred children.
 */
const KEEP_FILTER_ON_PAPER = [
  '.MuiAppBar-root',
  '.MuiDrawer-paper',
  '.MuiPopover-paper',
  '.MuiMenu-paper',
  '.MuiDialog-paper',
  '.MuiAutocomplete-paper',
  '.MuiAccordion-root',
];

const Paper = (settings: Settings) => {
  const backdropFilter =
    settings.backgroundFilter && settings.backgroundFilter !== 'none'
      ? settings.backgroundFilter
      : undefined;

  if (!backdropFilter) {
    return {
      MuiPaper: {
        defaultProps: {
          variant: settings.skin === 'bordered' ? 'outlined' : 'elevation',
        },
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
    };
  }

  const keepOnPaper = KEEP_FILTER_ON_PAPER.map((cls) => `&${cls}`).join(', ');
  const everythingElse = `&${KEEP_FILTER_ON_PAPER.map((cls) => `:not(${cls})`).join('')}`;

  return {
    MuiPaper: {
      defaultProps: {
        variant: settings.skin === 'bordered' ? 'outlined' : 'elevation',
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          [keepOnPaper]: {
            backdropFilter,
          },
          // An element with `backdrop-filter` becomes a backdrop root, and its descendants then
          // sample nothing — so a Paper that blurred itself silently killed the glass on every
          // toolbar and sticky header inside it. Painting the blur on a child instead keeps the
          // frosted surface without swallowing the blurs above it.
          [everythingElse]: {
            position: 'relative',
            isolation: 'isolate',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              borderRadius: 'inherit',
              backdropFilter,
            },
          },
        },
      },
    },
  };
};

export default Paper;
